import { Link } from "@tanstack/react-router";
import { m } from "@/paraglide/messages";
import { groupByLength } from "../lib/unscramble";

function WordChips({
  words,
  highlight = false,
}: {
  words: string[];
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {words.map((w) => (
        <Link
          key={w}
          to="/word-unscrambler/$letters"
          params={{ letters: w }}
          className={`rounded-md border px-2.5 py-1 text-sm transition-colors hover:border-primary/60 ${
            highlight
              ? "border-primary/60 bg-primary/10 text-primary"
              : "border-border bg-card/40"
          }`}
        >
          {w}
        </Link>
      ))}
    </div>
  );
}

export interface WordResultsProps {
  letters: string;
  words: string[];
  anagrams: string[];
  total: number;
}

export function WordResults({
  letters,
  words,
  anagrams,
  total,
}: WordResultsProps) {
  if (letters.length < 2) {
    return <p className="text-muted-foreground">{m.wu_enter_letters()}</p>;
  }
  if (words.length === 0) {
    return (
      <p className="text-muted-foreground">
        {m.wu_no_results({ letters: letters.toUpperCase() })}
      </p>
    );
  }

  const groups = groupByLength(words);

  return (
    <div className="space-y-8">
      <p className="pct-result text-sm font-semibold">
        {m.wu_found({ count: String(total), letters: letters.toUpperCase() })}
      </p>

      {anagrams.length > 0 && (
        <section className="pct-panel space-y-3 rounded-xl p-5">
          <h2 className="font-display text-xl font-bold tracking-tight">
            {m.wu_anagrams_title()}
          </h2>
          <WordChips words={anagrams} highlight />
        </section>
      )}

      {groups.map((g) => (
        <section key={g.length} className="pct-panel space-y-3 rounded-xl p-5">
          <h2 className="font-display text-xl font-bold tracking-tight">
            {m.wu_n_letter_words({ n: String(g.length) })}
          </h2>
          <WordChips words={g.words} />
        </section>
      ))}
    </div>
  );
}
